import { ReactElement, useState } from 'react';
import { Svg, Text } from '@hopskipdrive/react-components';
import { Check, ErrorOutline } from '@mui/icons-material';
import { Select } from '@mui/material';

import {
  CloseButton,
  StatusMenuHeading,
  StatusMenuItem,
  StatusSelectContainer,
  StyledStatus,
} from './StatusSelect.styles';

export interface StatusSelectOption {
  label: string;
  value: string;
}

export interface StatusSelectProps {
  options: StatusSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
}

interface StatusProps {
  value: string;
  label?: string;
  filled?: boolean;
}

export const Status = ({ value, label, filled }: StatusProps): ReactElement => {
  const mapStatusToIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <ErrorOutline />;
      case 'resolved':
        return <Check />;
      default:
        return undefined;
    }
  };

  return (
    <StyledStatus className={'status ' + value + ' ' + (filled ? 'filled' : '')}>
      {mapStatusToIcon(value)}
      <Text variant="subtitle1">{label ? label : value}</Text>
    </StyledStatus>
  );
};

// TODO: Ideally, we should align this with /features/FilterPanel/StatusFilter
// to prevent duplicated code.

const StatusSelect = ({ options, value, onChange }: StatusSelectProps): ReactElement => {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleChange = (event) => {
    onChange(event.target.value);
    setMenuOpen(false);
  };

  const toggleMenu = (state) => () => {
    setMenuOpen(state);
  };

  return (
    <StatusSelectContainer className={value || ''}>
      <Select
        label="label"
        displayEmpty
        MenuProps={{
          PaperProps: {
            style: {
              width: 180,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          BackdropProps: {
            style: {
              opacity: 0,
            },
          },
        }}
        labelId="status-select-label"
        value={value || ''}
        onChange={handleChange}
        open={menuOpen}
        onOpen={toggleMenu(true)}
        onClose={toggleMenu(false)}
        renderValue={(selected) => {
          const selectedOption = options.find((option) => option.value === selected);

          return selectedOption ? <Status value={selectedOption.value} label={selectedOption.label} /> : null;
        }}
      >
        <StatusMenuHeading>
          <Text variant="subtitle2">Select Status</Text>
          <CloseButton onClick={toggleMenu(false)}>
            <Svg icon="cross" />
          </CloseButton>
        </StatusMenuHeading>
        {options.map((option) => (
          <StatusMenuItem key={option.value} value={option.value}>
            <Status value={option.value} label={option.label} filled />
          </StatusMenuItem>
        ))}
      </Select>
    </StatusSelectContainer>
  );
};

export default StatusSelect;
